import React, { useEffect, useState } from "react";
import "../ReturnExchange/ReturnExchange.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import API from "../../../app/api";
import Footer from "../../../components/Footer/Footer";
import Breadcrumbs from "../../../components/Breadcrumbs/Breadcrumbs";

const Returnexchange = () => {
  const [option, setOption] = useState("return");
  const [reason, setReason] = useState("");
  const [comments, setComments] = useState("");
  const [reasonList, setReasonList] = useState([]);
  const [showcnModal, setShowcnModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);

  const { state } = useLocation();
  const navigate = useNavigate();

  const items = Array.isArray(state?.item) ? state.item : [];
  const orderNo = state?.orderNo || "";
  const selectOption = state?.selectOption || "return";

  // -------------------- EFFECTS --------------------
  useEffect(() => {
    document.title = "Obsession - Return / Exchange";
    getReason("return");
  }, []);

  useEffect(() => {
    setOption(selectOption);
    getReason(selectOption);
  }, [selectOption]);

  // Auto-select only eligible items
  useEffect(() => {
    const allowedIds = items
      .filter((i) => (option === "return" ? i.can_return : i.can_exchange))
      .map((i) => i.itemId);
    setSelectedItems(allowedIds);
  }, [items, option]);

  // -------------------- GET REASONS --------------------
  const getReason = async (type) => {
    try {
      const url = type === "return" ? "/reasons/return" : "/reasons/exchange";
      const res = await API.get(url);
      if (res?.data?.status === 200) {
        setReasonList(res.data.data || []);
      } else {
        setReasonList([]);
      }
    } catch (err) {
      console.error(err);
      setReasonList([]);
    }
  };

  // -------------------- CHECKBOX HANDLERS --------------------
  const handleItemCheck = (itemId) => {
    setSelectedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    const allowedIds = items
      .filter((i) => (option === "return" ? i.can_return : i.can_exchange))
      .map((i) => i.itemId);

    if (selectedItems.length === allowedIds.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allowedIds);
    }
  };

  // -------------------- SUBMIT --------------------
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (selectedItems.length === 0) {
      toast.error("Please select at least one product");
      setShowcnModal(false);
      return;
    }

    if (!reason) {
      toast.error("Please select a reason");
      return;
    }

    if (!comments || comments.length < 5) {
      toast.error("Comments must be at least 5 characters");
      return;
    }

    setLoading(true);

    try {
      setLoading(true);

      const payload = {
        item_ids: selectedItems,
        reason_id: reason,
        remarks: comments,
      };

      let res;
      if (option === "return") {
        res = await API.put(`/orders/${orderNo}/return`, payload);
      } else {
        res = await API.put(`/orders/${orderNo}/exchange`, payload);
      }

      if (res?.data?.success) {
        // Redirect to the specific order detail page dynamically
        navigate(`/accounts/orders/${orderNo}`, {
          state: {
            orderNo,
            itemIds: selectedItems,
            page: option === "return" ? "Return" : "Exchange",
          },
        });
      }
    } catch (err) {
      console.error(err);
      // Fallback: main orders page
      navigate(`/accounts/orders/${orderNo}`, {
        replace: true,
        state: { cancelled: true },
      });
    } finally {
      setLoading(false);
      setShowcnModal(false);
    }
  };

  // Breadcrumb paths
  const breadcrumbPaths = [
    { label: "Orders", to: "/accounts/orders" },
    { label: "Order Detail", to: `/accounts/orders/${orderNo}` },
  ];

  // -------------------- JSX --------------------


  return (
    <>
      <div>
        <Breadcrumbs paths={breadcrumbPaths} />
      </div>
      <div className="return-exchange-container">

        {/* LEFT SIDE */}
        <div className="return-exchange-form">
          <h2>RETURN AND EXCHANGE</h2>

          <p className="note">
            <i>ℹ After delivery, you're eligible for return or exchange within 5 days.</i>
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setShowcnModal(true);
            }}
          >
            <label>
              Reason for {option} <span className="required">*</span>
            </label>

            <select value={reason} onChange={(e) => setReason(e.target.value)}>
              <option value="">Select {option}</option>
              {reasonList.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.reason}
                </option>
              ))}
            </select>

            <label>
              Comments <span className="required">*</span>
            </label>

            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
            />

            <div className="refund-info">
              <small>
                By clicking “{option} Order”, I agree to{" "}
                <Link to="/tc-of-sale">Terms of Service</Link>
              </small>
            </div>

            <button type="submit" disabled={loading} className="return-btn">
              {loading ? "Please wait..." : `${option} Order`}
            </button>
          </form>

          {/* CONFIRM MODAL */}
          {showcnModal && (
            <div className="modal-overlay-history">
              <div className="modal-box">
                <h3>{option} Order</h3>
                <p>Are you sure you want to {option} selected products?</p>
                <div className="modal-actions">
                  <button onClick={() => setShowcnModal(false)}>
                    DON’T {option.toUpperCase()}
                  </button>
                  <button className="proceed" onClick={handleSubmit}>
                    {option.toUpperCase()} ORDER
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className="cancel-order-details">
          <h3 style={{ borderBottom: "1px solid #e4e4e4", padding: "10px 0" }}>Order Details</h3>

          {items.map((item, idx) => (
            <div>

              <div className="order-box" key={idx}>

                {/* Checkbox for all items, but disabled if not eligible */}
                <input
                  type="checkbox"
                  checked={selectedItems.includes(item.id)}
                  disabled={
                    (option === "return" && !item.allow_return) ||
                    (option === "exchange" && !item.allow_exchange)
                  }
                  onChange={() => handleItemCheck(item.id)}
                />

                <div className="order-info cancel-products-box">
                  <p><b>{item.product_name}</b></p>
                  <p>Qty: {item.quantity}</p>
                  <p>Size: {item.size}</p>
                  <p>Color: {item.color}</p>
                  <p>Price: ₹{item.mrp}</p>
                </div>

                <img src={item.product_media} alt={item.product_name} className="img_cancel" />
              </div>
              <div>
                {/* Message always at bottom */}
                {((option === "return" && !item.allow_return) ||
                  (option === "exchange" && !item.allow_exchange)) && (
                    <div style={{ color: "red", fontSize: "14px", marginTop: "5px", marginBottom: "10px" }}>
                      This product is not eligible for Return / Exchange
                    </div>
                  )}
              </div>

              {/* Show “Return till” / “Exchange till” only for eligible products */}

              {option === "return" && item.allow_return && item.valid_returnable_date && (
                <p style={{ color: "green", marginTop: "5px" }}>
                  Return till{" "}
                  {new Date(item.valid_returnable_date).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              )}

              {option === "exchange" && item.allow_exchange && item.valid_exchange_date && (
                <p style={{ color: "green", marginTop: "5px" }}>
                  Exchange till{" "}
                  {new Date(item.valid_exchange_date).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              )}
            </div>

          ))}
        </div>
      </div>
      <Footer />
    </>
  );
};
export default Returnexchange;